import React, { useMemo, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  buildRangePreviewTable,
  validateRangeDefinition,
} from "../services/parametricRangeTableEngine";
import useParameterStore from "../../store/parameterStore";

const normalizeParam = (row = {}) => {
  const range = Array.isArray(row.ranges) && row.ranges.length
    ? row.ranges[0]
    : {};

  return {
    id: row.id || "",
    name: row.name || "",
    type: row.type || "I",
    defaultValue: row.defaultValue ?? "",
    groupName: row.groupName || "",
    rangeName: range.rangeName || "",
    step: range.step || "",
    rangeValue: range.rangeValue || "",
  };
};

const getRangePlaceholder = (type) => {
  if (type === "I") return "1-10 or 1,2,3";
  if (type === "F") return "1-10 or 1.5,2.5";
  if (type === "B") return "0,1 or T,nil";
  if (type === "S") return "small,medium,large";
  return "range";
};




const RangeTabContent = () => {
  const storedGlobalParams = useParameterStore((state) => state.globalParameters);
  const storedLocalParams = useParameterStore((state) => state.localParameters);
  const setStoredGlobalParams = useParameterStore((state) => state.setGlobalParameters);
  const setStoredLocalParams = useParameterStore((state) => state.setLocalParameters);
  const [showTablePopup, setShowTablePopup] = useState(false);

  const globalParams = useMemo(
    () => storedGlobalParams
      .map(normalizeParam)
      .filter((param) => param.type !== "LPP")
      .filter((param) => String(param.name || "").trim()),
    [storedGlobalParams]
  );
  const localParams = useMemo(
    () => storedLocalParams
      .map(normalizeParam)
      .filter((param) => param.type !== "LPP")
      .filter((param) => String(param.name || "").trim()),
    [storedLocalParams]
  );
  const previewTable = buildRangePreviewTable([...globalParams, ...localParams]);

  const updateRangeRow = (scope, rowId, patch) => {
    const sourceRows = scope === "global" ? storedGlobalParams : storedLocalParams;
    const setter = scope === "global" ? setStoredGlobalParams : setStoredLocalParams;

    setter(
      sourceRows.map((row) => {
        if (row.id !== rowId) return row;

        const currentRange = Array.isArray(row.ranges) && row.ranges.length
          ? row.ranges[0]
          : {};
        const nextRow = { ...row };

        if (Object.prototype.hasOwnProperty.call(patch, "groupName")) {
          nextRow.groupName = patch.groupName;
        }

        nextRow.ranges = [
          {
            ...currentRange,
            ...(Object.prototype.hasOwnProperty.call(patch, "rangeName")
              ? { rangeName: patch.rangeName }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(patch, "step")
              ? { step: patch.step }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(patch, "rangeValue")
              ? { rangeValue: patch.rangeValue }
              : {}),
          },
          ...(Array.isArray(row.ranges) ? row.ranges.slice(1) : []),
        ];

        return nextRow;
      })
    );
  };



const downloadTableAsPdf = async () => {
  const tableEl = document.getElementById("generated-range-table-pdf");
  if (!tableEl) return;

  try {
    const canvas = await html2canvas(tableEl, {
      backgroundColor: "#000000",
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 12;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 6;

    pdf.addImage(imgData, "PNG", 6, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 6;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 6, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save("generated_range_table.pdf");
  } catch (err) {
    console.error("❌ Failed to download range table PDF:", err);
  }
};


const renderSectionHeader = (title) => {
  return (
    <div style={sectionHeaderStyle}>
      <div style={sectionTitleStyle}>{title}</div>

      <div style={sectionActionsStyle}>
        <button
          type="button"
          style={sectionShowTableButtonStyle}
          onClick={() => setShowTablePopup(true)}
        >
          Show Table
        </button>
      </div>
    </div>
  );
};

const renderRangeRows = (scope, list) => {
  if (!list.length) {
    return <div style={emptyStyle}>No {scope} parameters are created</div>;
  }

  return (
    <>
      <div style={{ ...columnLabelRow, gap: "6px" }}>
        <div style={{ width: "95px" }}>Param</div>
        <div style={{ width: "65px" }}>GrpName</div>
        <div style={{ width: "55px" }}>R-Name</div>
        <div style={{ width: "40px" }}>Step</div>
        <div style={{ flex: 1 }}>R-Value</div>
      </div>

      {list.map((row, index) => {
        const validation = validateRangeDefinition(
          row.type,
          row.rangeValue,
          row.step
        );
        const generatedErrors = previewTable.parameterErrors[row.id] || {};
        const stepError = generatedErrors.step || validation.stepError;
        const rangeError =
          generatedErrors.rangeValue || validation.rangeValueError;
        const stepOk = !stepError;
        const rangeOk = !rangeError;

        return (
          <div key={row.id || index} style={{ ...rowStyle, gap: "6px" }}>
            
            {/* 🔥 CLEAN UI HACK: Param Name aur Type ek single Pill me */}
            <div
              title={`Param: ${row.name} | Type: ${row.type}`}
              style={{
                display: "flex",
                alignItems: "center",
                width: "95px",
                height: "30px",
                backgroundColor: "#222",
                border: "1px solid #333",
                borderRadius: "3px",
                padding: "0 6px",
                boxSizing: "border-box",
                cursor: "default"
              }}
            >
              <span style={{ flex: 1, color: "#ccc", fontSize: "11px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.name}
              </span>
              <span style={{ backgroundColor: "#111", color: "#00bcd4", fontSize: "10px", fontWeight: "bold", padding: "2px 4px", borderRadius: "3px", marginLeft: "4px" }}>
                {row.type}
              </span>
            </div>

            <input
              value={row.groupName || ""}
              onChange={(e) =>
                updateRangeRow(scope, row.id, { groupName: e.target.value })
              }
              placeholder="grp"
              style={{ ...inputStyle, width: "65px", padding: "4px 6px" }}
            />

            <input
              value={row.rangeName}
              onChange={(e) =>
                updateRangeRow(scope, row.id, { rangeName: e.target.value })
              }
              placeholder="r1"
              style={{ ...inputStyle, width: "55px", padding: "4px 6px" }}
            />

            <input
              value={row.step}
              disabled={row.type === "S"}
              onChange={(e) =>
                updateRangeRow(scope, row.id, { step: e.target.value })
              }
              placeholder={row.type === "S" ? "-" : "1"}
              title={stepError}
              style={{
                ...inputStyle,
                width: "40px",
                opacity: row.type === "S" ? 0.55 : 1,
                border: stepOk ? "1px solid #444" : "1px solid #ff3b3b",
                padding: "4px 6px",
                textAlign: "center"
              }}
            />

            <input
              value={row.rangeValue}
              onChange={(e) =>
                updateRangeRow(scope, row.id, { rangeValue: e.target.value })
              }
              placeholder={getRangePlaceholder(row.type)}
              title={rangeError}
              style={{
                ...inputStyle,
                flex: 1,
                minWidth: "50px",
                border: rangeOk ? "1px solid #444" : "1px solid #ff3b3b",
                padding: "4px 6px",
              }}
            />
          </div>
        );
      })}
    </>
  );
};


  return (
      <div className="range-tab-root" style={containerStyle}>
  <div style={topBarStyle}>
  <div style={titleStyle}>Range</div>
</div>

    <div style={rangeSectionsStyle}>
  <div style={rangeSectionStyle}>
    {renderSectionHeader("Global Parameters")}

    <div className="range-section-scroll" style={sectionBodyStyle}>
      {renderRangeRows("global", globalParams)}
    </div>
  </div>

  <div style={rangeSectionStyle}>
    {renderSectionHeader("Local Parameters")}

    <div className="range-section-scroll" style={sectionBodyStyle}>
      {renderRangeRows("local", localParams)}
    </div>
  </div>
</div>

{showTablePopup && (
  <div style={popupOverlayStyle}>
    <div style={popupCardStyle}>
      <div style={popupHeaderStyle}>
  <div>
    <div style={popupTitleStyle}>Generated Range Table</div>
    <div style={popupSubtitleStyle}>
      RangeName, Group aur parameter values ka preview.
    </div>
  </div>

  <div style={popupHeaderActionsStyle}>
    <button
      type="button"
      style={popupDownloadButtonStyle}
      onClick={downloadTableAsPdf}
    >
      Download PDF
    </button>

    <button
      type="button"
      style={popupCloseButtonStyle}
      onClick={() => setShowTablePopup(false)}
    >
      ×
    </button>
  </div>
</div>

      <div id="generated-range-table-pdf" style={popupTableWrapStyle}>
        <table style={previewTableStyle}>
          <thead>
            <tr>
              <th style={previewThStyle}>group</th>
              <th style={previewThStyle}>rangeName</th>
              {previewTable.columns.map((column) => (
                <th key={column} style={previewThStyle}>
                  {column}
                </th>
              ))}
            </tr>

            <tr>
              <th style={previewTypeThStyle}>string</th>
              <th style={previewTypeThStyle}>string</th>
              {previewTable.typeRow.map((type, index) => (
                <th key={`${type}-${index}`} style={previewTypeThStyle}>
                  {type}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {previewTable.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={previewTable.columns.length + 2}
                  style={previewEmptyTdStyle}
                >
                  {previewTable.hasErrors
                    ? "Fix the highlighted range values before generating the table."
                    : "No table data. Please add RangeName, Step and RangeValue."}
                </td>
              </tr>
            ) : (
              previewTable.rows.map((row, rowIndex) => (
                <tr key={`${row.rangeName}-${rowIndex}`}>
                  <td style={previewTdStyle}>{row.group}</td>
                  <td style={previewTdStyle}>{row.rangeName}</td>

                  {previewTable.columns.map((column) => (
                    <td key={column} style={previewTdStyle}>
                      {row.values[column]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

const containerStyle = {
  padding: "8px",
  color: "#ddd",
  fontSize: "12px",
  height: "calc(100dvh - 250px)",
  maxHeight: "calc(100dvh - 250px)",
  overflow: "hidden",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};


const topBarStyle = {
  display: "flex",
  alignItems: "center",
  height: "22px",
  minHeight: "22px",
  marginBottom: "6px",
  flexShrink: 0,
};

const titleStyle = {
  color: "#00bcd4",
  fontWeight: "bold",
  fontSize: "14px",
};

const subtitleStyle = {
  color: "#aaa",
  fontSize: "11px",
  marginTop: "2px",
};

const columnLabelRow = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "6px",
  color: "#aaa",
  fontSize: "12px",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "6px",
};

const inputStyle = {
  backgroundColor: "#2a2a2a",
  color: "#fff",
  border: "1px solid #444",
  borderRadius: "3px",
  padding: "6px",
  fontSize: "12px",
  height: "30px",
  boxSizing: "border-box",
};

const smallButtonStyle = {
  backgroundColor: "#2c2c2c",
  color: "#fff",
  border: "1px solid #444",
  borderRadius: "4px",
  padding: "5px 8px",
  cursor: "pointer",
  fontSize: "12px",
};

const showTableButtonStyle = {
  backgroundColor: "#123b20",
  color: "#b6ffcb",
  border: "1px solid #1f8f46",
  borderRadius: "4px",
  padding: "5px 8px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold",
};

const popupOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.78)",
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};

const popupCardStyle = {
  width: "86vw",
  height: "78vh",
  backgroundColor: "#000000",
  border: "1px solid #0f7a35",
  borderRadius: "10px",
  boxShadow: "0 0 20px rgba(0, 255, 120, 0.12)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const popupHeaderStyle = {
  height: "56px",
  minHeight: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 14px",
  borderBottom: "1px solid #0f7a35",
  backgroundColor: "#050505",
};

const popupTitleStyle = {
  color: "#00ff77",
  fontSize: "15px",
  fontWeight: "bold",
};

const popupSubtitleStyle = {
  color: "#bdbdbd",
  fontSize: "11px",
  marginTop: "2px",
};

const popupHeaderActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const popupDownloadButtonStyle = {
  height: "30px",
  backgroundColor: "#101010",
  color: "#00ff77",
  border: "1px solid #0f7a35",
  borderRadius: "6px",
  padding: "0 10px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold",
};

const popupCloseButtonStyle = {
  width: "30px",
  height: "30px",
  backgroundColor: "#101010",
  color: "#00ff77",
  border: "1px solid #0f7a35",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "20px",
  lineHeight: "20px",
};

const popupTableWrapStyle = {
  flex: 1,
  overflow: "auto",
  padding: "10px",
  backgroundColor: "#000000",
};

const previewTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
  color: "#ffffff",
  backgroundColor: "#000000",
};

const previewThStyle = {
  backgroundColor: "#050505",
  color: "#00ff77",
  border: "1px solid #0f7a35",
  padding: "9px",
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const previewTypeThStyle = {
  backgroundColor: "#000000",
  color: "#ffffff",
  border: "1px solid #0f7a35",
  padding: "8px",
  textAlign: "left",
  position: "sticky",
  top: "36px",
  zIndex: 2,
};

const previewTdStyle = {
  backgroundColor: "#000000",
  border: "1px solid #0b3d1e",
  padding: "8px",
  color: "#ffffff",
};

const previewEmptyTdStyle = {
  backgroundColor: "#000000",
  border: "1px solid #0b3d1e",
  padding: "18px",
  color: "#bdbdbd",
  textAlign: "center",
};



const emptyStyle = {
  color: "#999",
  border: "1px dashed #444",
  borderRadius: "5px",
  padding: "8px",
};



const rangeSectionsStyle = {
  flex: 1,
  minHeight: 0,
  maxHeight: "100%",
  display: "grid",
  gridTemplateRows: "minmax(0, 1fr) minmax(0, 1fr)",
  gap: "8px",
  overflow: "hidden",
};

const rangeSectionStyle = {
  border: "1px solid #333",
  borderRadius: "6px",
  padding: "8px",
  overflow: "hidden",
  backgroundColor: "#1b1b1b",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  height: "100%",
  maxHeight: "100%",
  boxSizing: "border-box",
};

const sectionTitleStyle = {
  color: "#00bcd4",
  fontWeight: "bold",
  fontSize: "13px",
};


const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  minHeight: "30px",
  marginBottom: "6px",
};

const sectionActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
};

const sectionBodyStyle = {
  flex: 1,
  minHeight: 0,
  height: "100%",
  maxHeight: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  paddingRight: "2px",
  scrollbarWidth: "thin",
  scrollbarColor: "#111 #000",
};

const sectionShowTableButtonStyle = {
  backgroundColor: "#101010",
  color: "#00ff77",
  border: "1px solid #0f7a35",
  borderRadius: "4px",
  padding: "4px 7px",
  cursor: "pointer",
  fontSize: "11px",
  fontWeight: "bold",
};

export default RangeTabContent;
